import { createSelector } from 'reselect'
import Path from 'webmango-utils/path'
import { getFileTypeSchemas } from './schema'
import { getDataDirectory } from './config'
import { getArgN, makeAPI } from './utils'
import * as Sample from './sample'
import { getAPI as getDatatypeAPI, getBaseFromDatatype } from './datatype'
import * as selfExports from './file'
import { isInitialised } from './mangoxml'

/**
 * File Listing
 */

const EMPTY = {}
const EMPTY_ARRAY = []

export const getAll = state => state.files || EMPTY
export const getCwd = state => state.fileBrowser.cwd

export { getFileTypeSchemas }

export const getAllList = createSelector([ getAll, getCwd ], (files, cwd) => {
  return (files[cwd] || EMPTY_ARRAY)
})

export const onlyFiles = createSelector([ getAllList ], (files) => {
  return files.filter(f => !f.isDir)
})

export const onlyDirs = createSelector([ getAllList ], (files) => {
  return files.filter(f => f.isDir)
})

export const getCurrentDir = createSelector([ onlyDirs ], (dirs) => {
  return dirs[0]
})

export const getDataDirectoryInfo = createSelector([
  ({ fileInfo }) => fileInfo,
  getDataDirectory
], (fileInfo, dataDirectory) => {
  return fileInfo[dataDirectory]
})

export const hasLoadedDataDirectory = createSelector([
  ({ files }) => files,
  getDataDirectory
], (files, dataDirectory) => {
  return files[dataDirectory]
})

export const hasLoadedInputFile = createSelector([
  ({ fileInfo }) => fileInfo,
  Sample.getForProcess,
  (state, processID) => state.processes[processID]
], (fileInfo, sample, process) => {
  const inputNode = process.nodes[0]
  if (!inputNode) return true
  const filename = inputNode.value['inputdata/filename']
  if (!filename) return true // nothing to load
  const filepath = Path.resolve(sample.localSamplePath, filename)
  // true so long as there's a value
  // for missing files stat is null, but it should be considered loaded
  return fileInfo[filepath] && (fileInfo[filepath].stat || fileInfo[filepath].stat === null)
})

export const getIsProcessLoading = createSelector([
  hasLoadedDataDirectory,
  hasLoadedInputFile,
  (state, processID) => {
    return isInitialised(state, state.processes[processID])
  }
], (hasLoadedDataDirectory, hasLoadedInputFile, isMangoInitialised) => {
  return (
    !hasLoadedDataDirectory ||
    !hasLoadedInputFile ||
    !isMangoInitialised
  )
})

const COMPRESSION_EXTENSIONS = new Set(['gz', 'bz2', 'Z', 'zip'])

/**
 * Check if `filepath` looks 'compressed'
 *
 * @param {String} filepath
 * @returns {Boolean}
 */

export function isCompressed (filepath) {
  return COMPRESSION_EXTENSIONS.has(Path.extname(filepath).slice(1))
}

function removeExtension (path, ext = Path.extname(path)) {
  return Path.join(
    Path.dirname(path),
    Path.basename(path, ext)
  )
}

function trimmedFilepath (filepath) {
  if (!isCompressed(filepath)) return filepath
  // chop off compression extension
  return removeExtension(filepath)
}

const getFilepath = createSelector([ getArgN(1) ], (filepath) => {
  return trimmedFilepath(filepath)
})

export const getFileTypes = getFileTypeSchemas

export const getFiletypesForDatatype = createSelector([ getFileTypeSchemas, getArgN(1) ], (filetypes, datatype) => {
  return filetypes.filter(f => (
    f.datatypes.find(d => d === datatype)
  ))
})

export const getDataExtensions = createSelector([ getFileTypeSchemas ], (filetypes) => {
  return Array.from(filetypes.reduce((s, t) => {
    t.extensions.forEach(ext => s.add(ext))
    return s
  }, new Set()))
})

/**
 * Check if `filepath` is a _dir as opposed to a .file
 *
 * Given name `getIsDirFile` over `isDir` to be clear this isn't
 * regular dir checking.
 *
 * @param {String} filepath
 * @returns {Boolean}
 */

export const getIsDirFile = createSelector([ getDataExtensions, getFilepath ], (dataExtensions, filepath) => {
  return dataExtensions.some(ext => filepath.endsWith(`_${ext}`))
})

export const getFullExtension = createSelector([ getDataExtensions, getFilepath, getIsDirFile ], (dataExtensions, filepath, isDirFile) => {
  const ext = dataExtensions.find(ext => (
    filepath.endsWith(`.${ext}`) ||
    filepath.endsWith(`_${ext}`)
  ))

  if (!ext) return Path.extname(filepath)

  return (
    isDirFile
      ? `_${ext}`
      : `.${ext}`
  )
})

/**
 * Extract extension from `filepath`, without the dot.
 * Also handles 'dir extensions'
 * e.g.
 * these should be treated the same: segmented_SS.nc & segmented_SS_nc
 *
 * @param {String} filepath
 * @throws AssertionError If no extension is found.
 * @returns {String}
 *
 * @see hasExtension
 */

export const getExtension = createSelector([ getFullExtension ], (extension) => {
  return extension.slice(1) // remove leading . or _
})

/**
 * Get filename of `filepath`.
 * i.e. remove directory and extension.
 *
 * @param {String} filepath
 * @returns {String}
 */

export const getFileName = createSelector([ getFullExtension, getFilepath ], (extension, filepath) => {
  return Path.basename(filepath, extension)
})

export const getBase = createSelector([ getDatatypeAPI, getFileName ], (datatypeAPI, fileName) => {
  return datatypeAPI.getBase(fileName)
})

export { getBaseFromDatatype }

export const getExtensionFiletype = createSelector([ getFileTypeSchemas, (_, extension) => extension ], (filetypes, extension) => {
  return filetypes.find(filetype => (
    filetype.extensions.includes(extension)
  ))
})

/**
 * Get data type of `filepath`.
 *
 * @param {String} filepath
 * @throws {AssertionError} If `filepath` has no datatype.
 * @see hasDatatype
 * @returns {String}
 */

export const getDatatype = createSelector([ getDatatypeAPI, getBase, getExtension ], (datatypeAPI, base, ext, name) => {
  if (ext === 'csv') return 'csv_file'
  if (!base) return ''
  return datatypeAPI.getDatatypeFromBase(base)
})

export const getFiletype = createSelector([ getFileTypeSchemas, getExtension, getDatatype, (_, s) => s ], (filetypes, extension, datatype, file) => {
  return filetypes.find(filetype => (
    filetype.extensions.includes(extension) && filetype.datatypes.includes(datatype)
  ))
})

export const getIsValidExtension = createSelector([ getFiletype ], (filetype) => {
  return !!filetype
})

/**
 * True if filepath is an _nc dir or .nc file.
 */

export function isNetCDF (filepath = '') {
  return filepath.endsWith('_nc') || filepath.endsWith('.nc')
}

export const isDatatype = (state, { path, datatype, compressed }) => {
  if (compressed) {
    path = getFilepath(state, removeExtension(path, '.zip'))
  }
  // Relevant comment from mango.xml header:
  //
  // N.B. A file entry is a required field and the file must exist, and
  // saving will only succeed if the file's name matches the data type and
  // the file can be found.
  //
  // N.B. Predefined file types are "exe", "dir", "data" and "nc_data".  "dir"
  // must be a directory, "exe" and "data" must exist, but have no other
  // restrictions placed on them, unlike the mainpanel types like
  // "Tomographic_Data".  "nc_data" accepts any file or directory ending in
  // "nc", so can be used to allow more than one mainpanel type.

  if (!path) return false
  if (!datatype) return true
  const extension = Path.extname(path)
  switch (datatype) {
    case 'exe':
    case 'data': {
      return true
    }
    case 'csv_file': {
      // note: undocumented in qmango/mango.xml header
      return extension === '.csv'
    }
    case 'dir': {
      // note: not currently found in either
      // mango.xml or qmango.xml
      return extension === ''
    }
    case 'nc_data': {
      return isNetCDF(path) && getDatatype(state, path)
    }
    default : {
      // check for specific type e.g. Tomographic_Data
      return datatype === getDatatype(state, path)
    }
  }
}

/**
 * Get suffix component of `filepath`.
 * It is valid to have no suffix.
 *
 * @param {String} filepath
 * @returns {String}
 */

export const getSuffix = createSelector([ getFileName, getBase ], (filename, base) => {
  if (!base) return filename
  return filename.split(base).slice(1).join('') || ''
})

export const getAPI = makeAPI(selfExports, [
  getFileTypeSchemas,
  getDatatypeAPI,
  getAll
])



// WEBPACK FOOTER //
// ../webmango-core/selectors/file.js